import {Stack} from '@dropbox/dig-foundations';
import {t} from 'i18next';
import {useEffect, useRef, useState} from 'react';

import {ChatToolLine, ChatToolStep} from './ChatToolLine';

/** A pause between steps shorter than this shows no "Thinking" line, so hand-offs don't flash. */
const HOLD_MS = 400;

/** `value`, but it only turns true once it has stayed true for `ms`. */
const useSettledTrue = (value: boolean, ms: number): boolean => {
  const [settled, setSettled] = useState(false);
  useEffect(() => {
    if (!value) {
      setSettled(false);
      return;
    }
    const timer = setTimeout(() => setSettled(true), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return value && settled;
};

/**
 * A step that's still running after this shimmers, so the quick ones (most
 * lookups) don't flash a shimmer that's gone before it reads as anything.
 */
const SHIMMER_AFTER_MS = 800;

/** Past this, a running line counts its seconds, so a slow step reads as busy, not stuck. */
const COUNT_AFTER_SECONDS = 6;

/** How long each of `keys` has been on screen, in ms, ticking while `active`. */
const useMsShown = (keys: string[], active: boolean) => {
  const shownAt = useRef(new Map<string, number>());
  const [now, setNow] = useState(() => Date.now());
  for (const key of keys) {
    if (!shownAt.current.has(key)) {
      shownAt.current.set(key, Date.now());
    }
  }
  useEffect(() => {
    if (!active) {
      return;
    }
    // Often enough to start a shimmer near SHIMMER_AFTER_MS, not just on the second.
    const timer = setInterval(() => setNow(Date.now()), 200);
    return () => clearInterval(timer);
  }, [active]);
  return (key: string): number => {
    const since = shownAt.current.get(key);
    return since === undefined ? 0 : Math.max(0, now - since);
  };
};

/** Runs of bookkeeping (combining, aggregating, selecting columns) read as one line. */
const groupBookkeeping = (steps: ChatToolStep[]): ChatToolStep[] =>
  steps.reduce<ChatToolStep[]>((lines, step) => {
    if (!step.isBookkeeping) {
      return [...lines, step];
    }
    // The last step of a run holds the rows the agent ended up with.
    const organized = {
      ...step,
      label: t(step.isPending ? 'chat_tool_lines_organizing' : 'chat_tool_lines_organized'),
    };
    return lines[lines.length - 1]?.isBookkeeping
      ? [...lines.slice(0, -1), organized]
      : [...lines, organized];
  }, []);

/**
 * The agent's steps for a turn, one line each, as they happen: a step's line
 * appears as soon as the agent picks the tool, led by where it looks. Its
 * label shimmers once it's been running a moment (parallel steps all shimmer), with Dash's
 * searches under it, then says what it did and found ("Searched Knowledge
 * articles for “401k” · 5 found"). The lines stay once the answer comes.
 * While the agent decides what to do next, Ask's glyph shows on its own, and
 * after 6s says "Thinking · 6s". Anything still running after 6s counts its
 * seconds. A line opens to what the step was asked and, when the answer
 * doesn't show them, its rows.
 */
export const ChatToolLines = ({
  steps,
  isStreaming,
  working,
  idleLabel,
}: {
  steps: ChatToolStep[];
  isStreaming: boolean;
  /** The agent is busy with no other visible sign of it, e.g. before the answer starts. */
  working: boolean;
  /** What the agent is doing between steps, said right away, e.g. "Planning your app". */
  idleLabel?: string;
}) => {
  const lines = groupBookkeeping(steps);
  const live = isStreaming && (working || lines.some((step) => step.isPending));
  // Deciding what to do next: before any step, or once every step has returned.
  const deciding = live && !lines.some((step) => step.isPending);
  const settledDeciding = useSettledTrue(deciding, HOLD_MS);
  const thinking = deciding && (lines.length === 0 || settledDeciding);
  const stepKey = (index: number) => `step-${index}`;
  const thinkingKey = `thinking-${lines.length}`;
  const msShown = useMsShown(
    live
      ? [
          ...lines.flatMap((step, i) => (step.isPending ? [stepKey(i)] : [])),
          ...(deciding ? [thinkingKey] : []),
        ]
      : [],
    live
  );
  const timed = (key: string): string | undefined => {
    const seconds = Math.floor(msShown(key) / 1000);
    return seconds >= COUNT_AFTER_SECONDS ? t('chat_tool_lines_seconds', {seconds}) : undefined;
  };

  if (!live && lines.length === 0) {
    return null;
  }
  const thinkingFor = timed(thinkingKey);
  // Until 6s the glyph says it on its own.
  const thinkingLabel =
    (lines.length > 0 ? idleLabel : undefined) ?? (thinkingFor ? t('chat_thinking') : '');
  return (
    <Stack gap="2" role={live ? 'status' : undefined}>
      {lines.map((step, i) =>
        live && step.isPending ? (
          <ChatToolLine
            key={i}
            step={{...step, result: timed(stepKey(i))}}
            working
            shimmer={msShown(stepKey(i)) >= SHIMMER_AFTER_MS}
            glyph={false}
          />
        ) : (
          <ChatToolLine key={i} step={step} working={false} />
        )
      )}
      {thinking ? (
        <ChatToolLine
          step={{label: thinkingLabel, result: thinkingLabel ? thinkingFor : undefined}}
          working
        />
      ) : null}
    </Stack>
  );
};
